// The waiver a registration must carry.
//
// One resolver so every sign-up form asks for the same document the server will
// enforce. The client copy is for DISPLAY and for gating the button; the server
// re-reads settings itself, because a browser that names its own waiver could
// accept a blank one and still produce a signed-looking record.

import { collection, doc, getDoc, getDocs } from 'firebase/firestore';
import { db } from '../firebase';

/**
 * The waiver a registration must carry: the league's own if it names one, else
 * the club default.
 *
 * A league's own waiver is never silently swapped for the default. If it cannot
 * be read — a restricted document, or one deleted since — this returns
 * { unreadable: true } so the form can say so and stop. Falling back would show
 * one document while the server recorded acceptance of another, which is worse
 * than refusing: the record would name a waiver the player never saw.
 *
 * Null means no waiver is configured at all and registration proceeds without
 * one. The server takes the same view, so an unset setting never locks players
 * out.
 */
export async function loadRequiredWaiver(league) {
  const read = async (id) => {
    if (!id) return null;
    try {
      const d = await getDoc(doc(db, 'clubDocuments', id));
      if (!d.exists() || d.data().enabled === false) return null;
      return { id: d.id, name: d.data().name || 'Liability waiver', slug: d.data().slug || '' };
    } catch {
      return null;                    // denied by rules, or offline
    }
  };
  const ownId = league?.waiverDocId || '';
  if (ownId) {
    const own = await read(ownId);
    return own || { id: ownId, unreadable: true };
  }
  try {
    const settings = await getDoc(doc(db, 'settings', 'membershipTiers'));
    return read(settings.exists() ? (settings.data().requiredWaiverId || '') : '');
  } catch {
    return null;
  }
}

/**
 * Documents that can be offered as a league's waiver.
 *
 * Two filters, both load-bearing. actionType 'accept' is what marks a document
 * as something a person agrees to; the rest are there to be read. And the
 * audience must include 'everyone', because league sign-up is public — a
 * restricted document cannot be shown to someone without an account, which
 * would leave them asked to accept something they cannot open.
 */
export async function loadAcceptableDocs() {
  try {
    const snap = await getDocs(collection(db, 'clubDocuments'));
    return snap.docs.map(d => ({ id: d.id, ...d.data() }))
      .filter(d => d.actionType === 'accept' && d.enabled !== false
        && Array.isArray(d.audience) && d.audience.includes('everyone'))
      .map(d => ({ id: d.id, name: d.name || 'Untitled', slug: d.slug || '' }))
      .sort((a, b) => a.name.localeCompare(b.name));
  } catch {
    return [];
  }
}

/** Where a club document is read. Addressed by SLUG under /docs, not by id. */
export const waiverHref = (w) => (w?.slug ? `/docs/${w.slug}` : '');
